// src/pages/user/PaymentCallback.tsx
//
// Route: /user/payments/callback
// Paystack redirects here after payment with ?ref=FSL-xxx
//
// MULTI-CHILD PAYMENT QUEUE
// When a parent enrols multiple children, the flow is:
//   1. UserEnrolment creates all enrolments, stores IDs [1..n] in
//      localStorage under "fsl_payment_queue"
//   2. Paystack redirects to this page after child[0] pays
//   3. This page verifies child[0], then reads the queue
//   4. If more children remain: initialize the next payment,
//      redirect to Paystack again
//   5. Repeat until queue is empty, then show final success screen

import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useVerifyPaymentMutation, useInitializePaymentMutation } from "../../app/api/paymentApi";

import { ArrowRight, Check, CircleHelp, CreditCard, Hourglass, X } from "lucide-react";
/* ─────────────────────────────────────────────────────────────────
   QUEUE HELPERS
───────────────────────────────────────────────────────────────── */

const QUEUE_KEY = "fsl_payment_queue";

interface PaymentQueue {
  queue:       string[];   // remaining enrolment IDs to pay for
  childNames:  string[];   // names matching the queue positions
  totalCount:  number;     // total children being enrolled
  paidCount:   number;     // how many have been paid so far
}

function readQueue(): PaymentQueue | null {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PaymentQueue;
  } catch {
    return null;
  }
}

function writeQueue(q: PaymentQueue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

function clearQueue() {
  localStorage.removeItem(QUEUE_KEY);
}

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

type PageState =
  | "verifying"          // calling /verify
  | "initializing_next"  // calling /initialize for the next child
  | "success_partial"    // one paid, more pending, about to redirect
  | "success_all"        // all children paid
  | "pending"            // payment received but not yet confirmed
  | "failed"             // payment abandoned or declined
  | "no_ref";            // no ?ref= in the URL

interface PaidChild {
  childName: string;
  amount:    number;
  reference: string;
}

/* ─────────────────────────────────────────────────────────────────
   ATOMS
───────────────────────────────────────────────────────────────── */

function Spinner({ size = "lg" }: { size?: "sm" | "lg" }) {
  const cls = size === "lg" ? "h-16 w-16" : "h-5 w-5";
  return (
    <div className={`relative flex ${cls} items-center justify-center`}>
      <div className={`absolute ${cls} animate-spin rounded-full border-4 border-[#ebeff7] border-t-[#1b2d5e]`} />
      {size === "lg" && <span className="relative"><CreditCard className="h-7 w-7" aria-hidden /></span>}
    </div>
  );
}

function ProgressDots({ paid, total }: { paid: number; total: number }) {
  if (total <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <span key={i}
          className={[
            "h-2.5 w-2.5 rounded-full transition-all duration-500",
            i < paid   ? "bg-[#6cc2a0] scale-100" :
            i === paid ? "bg-[#1b2d5e] scale-125 shadow-[0_0_0_3px_rgba(27,45,94,0.2)]" :
                         "bg-[#dde2ec]",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SCREENS
───────────────────────────────────────────────────────────────── */

function VerifyingScreen({ childName, paidCount, totalCount }: { childName?: string; paidCount: number; totalCount: number }) {
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <Spinner />
      <h2 className="text-xl font-bold tracking-[-0.03em] text-[#16213e]">
        Verifying payment{childName ? ` for ${childName}` : ""}…
      </h2>
      <p className="max-w-xs text-sm leading-6 text-[#5f6780]">
        Confirming with Paystack. This takes just a moment.
      </p>
      {totalCount > 1 && (
        <div className="w-full space-y-2">
          <ProgressDots paid={paidCount} total={totalCount} />
          <p className="text-xs text-[#8189a0]">
            {paidCount} of {totalCount} payments confirmed
          </p>
        </div>
      )}
    </div>
  );
}

function InitializingNextScreen({ nextChildName, paidCount, totalCount }: { nextChildName: string; paidCount: number; totalCount: number }) {
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <Spinner />
      <h2 className="text-xl font-bold tracking-[-0.03em] text-[#16213e]">
        Preparing payment for {nextChildName}…
      </h2>
      <p className="max-w-xs text-sm leading-6 text-[#5f6780]">
        Redirecting you to Paystack for the next child.
      </p>
      <div className="w-full space-y-2">
        <ProgressDots paid={paidCount} total={totalCount} />
        <p className="text-xs text-[#8189a0]">
          {paidCount} of {totalCount} confirmed · {totalCount - paidCount} remaining
        </p>
      </div>
    </div>
  );
}

function SuccessAllScreen({ paidChildren, countdown }: { paidChildren: PaidChild[]; countdown: number }) {
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#edfaf4] text-4xl shadow-[0_0_0_8px_rgba(108,194,160,0.15)]">
        <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
      </span>
      <div>
        <h2 className="text-2xl font-bold tracking-[-0.04em] text-[#16213e]">
          {paidChildren.length > 1 ? "All enrolments confirmed!" : "Enrolment confirmed!"}
        </h2>
        <p className="mt-1 text-sm text-[#5f6780]">
          {paidChildren.length > 1
            ? `${paidChildren.length} children are now enrolled at Future Smart Lab.`
            : `${paidChildren[0]?.childName ?? "Your child"} is now enrolled.`}
        </p>
      </div>

      {/* Per-child confirmation list */}
      <div className="w-full space-y-2">
        {paidChildren.map((c, i) => (
          <div key={i} className="flex items-center gap-3 rounded-[16px] border border-[#b6e8d0] bg-[#edfaf4] px-4 py-3">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#6cc2a0] font-bold text-white"><Check className="h-3.5 w-3.5" aria-hidden /></span>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-[#16213e]">{c.childName}</p>
              <p className="font-mono text-[11.5px] text-[#5a6278]">{c.reference}</p>
            </div>
            <p className="text-sm font-bold text-[#2a7a56]">
              ₦{c.amount.toLocaleString("en-NG")}
            </p>
          </div>
        ))}
      </div>

      <p className="text-xs text-[#8189a0]">
        Redirecting to your enrolments in {countdown}s…
      </p>

      <Link to="/user/enrolments"
        className="mt-1 inline-flex items-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.30)] hover:bg-[#24397a]">
        View enrolments{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
      </Link>
    </div>
  );
}

function PendingScreen({ reference }: { reference: string }) {
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#fff8ee] shadow-[0_0_0_8px_rgba(240,160,80,0.12)]"><Hourglass className="h-10 w-10" aria-hidden /></span>
      <div>
        <h2 className="text-xl font-bold text-[#16213e]">Payment processing</h2>
        <p className="mt-1.5 max-w-xs text-sm leading-6 text-[#5f6780]">
          Your payment is being processed. Bank transfers can take a few minutes. Your enrolment will confirm automatically.
        </p>
      </div>
      <div className="w-full rounded-[20px] border border-[#f5d9a0] bg-[#fff8ee] px-5 py-4">
        <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">Reference</p>
        <p className="mt-1 font-mono text-sm text-[#7a4a10]">{reference}</p>
      </div>
      <div className="flex gap-3 w-full mt-2">
        <Link to="/user/enrolments"
          className="flex flex-1 items-center justify-center rounded-2xl bg-[#1b2d5e] px-5 py-3 text-sm font-semibold text-white hover:bg-[#24397a]">
          View enrolments
        </Link>
        <a href={`mailto:hello@futuresmartlab.com?subject=Payment%20${reference}`}
          className="flex flex-1 items-center justify-center rounded-2xl border border-[#dde2ec] px-5 py-3 text-sm font-semibold text-[#3b4560] hover:bg-[#f1f3f8]">
          Contact us
        </a>
      </div>
    </div>
  );
}

function FailedScreen({ reference, onRetry }: { reference: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#fff3f3] shadow-[0_0_0_8px_rgba(229,115,115,0.12)]"><X className="h-10 w-10" aria-hidden /></span>
      <div>
        <h2 className="text-xl font-bold text-[#16213e]">Payment not completed</h2>
        <p className="mt-1.5 max-w-xs text-sm leading-6 text-[#5f6780]">
          No money has been charged. You can return to your enrolments and try again.
        </p>
      </div>
      {reference && (
        <div className="w-full rounded-[20px] border border-[#f5c0c0] bg-[#fff5f5] px-5 py-3">
          <p className="text-xs text-[#9b2a2a]">Reference: <span className="font-mono">{reference}</span></p>
        </div>
      )}
      <div className="flex gap-3 w-full mt-2">
        <button onClick={onRetry}
          className="flex flex-1 items-center justify-center rounded-2xl bg-[#1b2d5e] px-5 py-3 text-sm font-semibold text-white hover:bg-[#24397a]">
          Back to enrolments
        </button>
        <Link to="/user/enrolments"
          className="flex flex-1 items-center justify-center rounded-2xl border border-[#dde2ec] px-5 py-3 text-sm font-semibold text-[#3b4560] hover:bg-[#f1f3f8]">
          Cancel
        </Link>
      </div>
    </div>
  );
}

function NoRefScreen() {
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f1f3f8]"><CircleHelp className="h-8 w-8" aria-hidden /></span>
      <div>
        <h2 className="text-lg font-bold text-[#16213e]">Nothing to verify</h2>
        <p className="mt-1 text-sm text-[#5f6780]">This page is only accessible after a Paystack payment.</p>
      </div>
      <Link to="/user/enrolments" className="rounded-2xl bg-[#1b2d5e] px-6 py-3 text-sm font-semibold text-white">
        Go to enrolments
      </Link>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ROOT PAGE
───────────────────────────────────────────────────────────────── */

export default function PaymentCallback() {
  const [searchParams]     = useSearchParams();
  const navigate           = useNavigate();
  const [verifyPayment]    = useVerifyPaymentMutation();
  const [initializePayment] = useInitializePaymentMutation();

  const reference = searchParams.get("ref") || searchParams.get("trxref") || "";

  const [pageState,    setPageState]    = useState<PageState>(reference ? "verifying" : "no_ref");
  const [paidChildren, setPaidChildren] = useState<PaidChild[]>([]);
  const [countdown,    setCountdown]    = useState(6);
  const [queueState,   setQueueState]   = useState<PaymentQueue | null>(null);
  // Name of the child whose payment is currently being processed
  const [currentChildName, setCurrentChildName] = useState<string | undefined>();
  const [nextChildName,    setNextChildName]     = useState<string>("");

  // ── Main orchestration effect ─────────────────────────────────────
  useEffect(() => {
    if (!reference) return;
    let cancelled = false;

    const run = async () => {
      // 1. Read queue from localStorage before verifying
      const queue = readQueue();
      setQueueState(queue);

      // 2. Verify the just-completed payment
      let verifyResult: any;
      try {
        const response = await verifyPayment({ reference }).unwrap();
        if (cancelled) return;
        verifyResult = response.data;
      } catch (err: any) {
        if (cancelled) return;
        const msg = err?.data?.message ?? "";
        if (msg.toLowerCase().includes("pending") || msg.toLowerCase().includes("processing")) {
          setPageState("pending");
        } else {
          setPageState("failed");
        }
        clearQueue();
        return;
      }

      if (verifyResult.payment?.status !== "paid") {
        setPageState("pending");
        clearQueue();
        return;
      }

      // 3. Record this child as paid
      const justPaid: PaidChild = {
        childName: verifyResult.child?.firstName ?? "Child",
        amount:    verifyResult.payment.amount,
        reference: verifyResult.payment.reference,
      };
      setCurrentChildName(justPaid.childName);
      setPaidChildren(prev => [...prev, justPaid]);

      // 4. Check if there are more enrolments in the queue
      if (queue && queue.queue.length > 0) {
        // Update queue: mark one more as paid
        const updatedQueue: PaymentQueue = {
          queue:      queue.queue.slice(1),           // remove first pending
          childNames: queue.childNames.slice(1),
          totalCount: queue.totalCount,
          paidCount:  queue.paidCount + 1,
        };

        const nextName = queue.childNames[0] ?? "next child";
        setNextChildName(nextName);
        setPageState("initializing_next");

        // Give the user a moment to see the progress before redirecting
        await new Promise(r => setTimeout(r, 1800));
        if (cancelled) return;

        // Initialize the next payment
        const nextEnrolmentId = queue.queue[0];
        try {
          const nextPayment = await initializePayment({ enrolmentId: nextEnrolmentId }).unwrap();

          // Persist the remaining queue (or clear if empty after this)
          if (updatedQueue.queue.length > 0) {
            writeQueue(updatedQueue);
          } else {
            clearQueue();
          }

          // Redirect to Paystack for next child
          window.location.href = nextPayment.data.authorizationUrl;
        } catch (err: any) {
          if (cancelled) return;
          // Failed to initialize next payment — show what was paid so far, clear queue
          clearQueue();
          setPageState("success_all"); // show partial success
        }
      } else {
        // All done — clear the queue and show final success
        clearQueue();
        setPageState("success_all");
      }
    };

    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  // ── Auto-redirect countdown after success_all ─────────────────────
  useEffect(() => {
    if (pageState !== "success_all") return;
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          navigate("/user/enrolments", { replace: true });
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [pageState, navigate]);

  const totalCount = queueState
    ? queueState.totalCount
    : paidChildren.length > 0 ? paidChildren.length : 1;
  const paidCount  = queueState
    ? queueState.paidCount
    : paidChildren.length;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f1f3f8] px-4 py-10">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="mb-8 flex justify-center">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#1b2d5e] text-base font-bold text-white shadow-[0_4px_14px_rgba(27,45,94,0.40)]">F</span>
            <div>
              <p className="text-[13px] font-bold leading-tight tracking-[-0.03em] text-[#16213e]">Future Smart Lab</p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#a6adbf]">Payment</p>
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-[28px] border border-[#dde2ec] bg-white px-8 py-10 shadow-[0_20px_60px_rgba(27,45,94,0.09)]">
          {pageState === "verifying" && (
            <VerifyingScreen
              childName={currentChildName}
              paidCount={paidCount}
              totalCount={totalCount}
            />
          )}
          {pageState === "initializing_next" && (
            <InitializingNextScreen
              nextChildName={nextChildName}
              paidCount={paidCount + 1}
              totalCount={totalCount}
            />
          )}
          {pageState === "success_partial" && (
            <VerifyingScreen
              childName={nextChildName}
              paidCount={paidCount + 1}
              totalCount={totalCount}
            />
          )}
          {pageState === "success_all" && (
            <SuccessAllScreen paidChildren={paidChildren} countdown={countdown} />
          )}
          {pageState === "pending" && <PendingScreen reference={reference} />}
          {pageState === "failed" && (
            <FailedScreen
              reference={reference}
              onRetry={() => navigate("/user/enrolments")}
            />
          )}
          {pageState === "no_ref" && <NoRefScreen />}
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-[12.5px] text-[#a6adbf]">
          Questions?{" "}
          <a href="mailto:hello@futuresmartlab.com" className="text-[#1b2d5e] hover:text-[#4a44b0]">
            hello@futuresmartlab.com
          </a>
        </p>
      </div>
    </div>
  );
}